import { useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "../../Providers/AuthProvider";
import LoadingSpinner from "../../Components/Components/LoadingSpinner";
import { useOrder } from "../../Providers/OrderProvider";
import ScrollUpArrow from "../../Components/Components/ScrollUpArrow";
import NoAccess from "../../Components/NoAccess";
import ErrorBoundary from "../../Components/ErrorBoundary/ErrorBoundary.jsx";
import useCheckUserRole from "../../Components/BasketPage/Hooks/useCheckUserRole.ts"
import { UserRole } from "../../Data/UserRolesEnum.ts";
import { useCart } from "../../Providers/CartProvider.jsx";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { IoCartOutline } from "react-icons/io5";
import YesNoCancelMessageModal from "../../Components/Components/Modals/YesNoCancelMessageModal.jsx";
import OrderTabsSelector from "./Components/OrderTabsSelector.jsx";
import ChangeOrderNumberModal from "./Components/ChangeOrderNumberModal.jsx";
import Orders from "./Components/Orders.jsx";
import axios from "axios";
import MobileAccountNavBar from "../../Components/MobileComponents/NavBarMobile.jsx/MobileAccountNavBar.jsx";
import { useParams } from "../../Providers/ParamsProvider.jsx";
import { AccountCurrentView } from "../../Data/PageModeEnums.ts";
import WzDocs from "./Components/WzDocs.jsx";

const OrdersPageIndex = () => {
    const observer = useRef()
    const { authState } = useAuth()
    const { changeCustOrderNumber, isLoadingOrders, orders, handleGetOrders, ordersHasMore, setOrders, handleGetWzDoc, setWzDocs, wzDocs, wzDocHasMore, isLoadingWzDoc } = useOrder()
    const [currentUser, setCurrentUser] = useState(authState.user ? authState.user : {})
    const [currentView, setCurrentView] = useState("Bieżące")
    const [isSaving, setIsSaving] = useState(false)
    const [isChangeNameVisible, setIsChangeNameVisible] = useState(false)
    const [handlingOrder, setHandlingOrder] = useState({ ordersNumber: '', custOrderNumber: '', siteId: '' })
    const [error, setError] = useState('')
    const { isUserHasAccess } = useCheckUserRole()
    const { loadBasketFromOrders, cartItems } = useCart()
    const [selectedOrders, setSelectedOrders] = useState([])
    const [showQuestionAboutReplaceBasket, setShowQuestionAboutReplaceBasket] = useState(false)
    const navigate = useNavigate()
    const { isMobile } = useParams()
    const [isLoadingBasket, setIsLoadingBasket] = useState(false)

    const [ordersFilterCriteria, setOrdersFilterCriteria] = useState({
        custId: authState.custId,
        dateFrom: null,
        dateTo: null,
        selectedSeller: '',
        selectedAddress: '',
        orderBy: 1,
        isArchive: false,
        selectedStatus: ''
    })

    const [wzDocsFilterCriteria, setWzDocsFilterCriteria] = useState({
        custId: authState.custId,
        dateFrom: null,
        dateTo: null,
        selectedSeller: '',
        selectedAddress: '',
        orderBy: 1,
        isArchive: false,
        selectedStatus: ''
    })

    const [pageNo, setPageNo] = useState(0)
    const [pageNoWzDocs, setPageNoWzDocs] = useState(0)

    useEffect(() => {
        if (currentUser === "") {
            window.location.href = '/login'
        }
    }, [currentUser])

    const lastBookElementRef = useCallback(node => {
        if (isLoadingOrders) {
            return
        }
        if (observer.current) {
            observer.current.disconnect()
        }
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && ordersHasMore) {
                setPageNo(prevPageNumber => prevPageNumber + 1)
            }
        })
        if (node) observer.current.observe(node)
    }, [isLoadingOrders, ordersHasMore])

    useEffect(() => {
        if (currentView === 'WZ') {
            setWzDocsFilterCriteria(prev => ({
                ...prev,
                isArchive: false
            }))
        }
        else {
            setOrdersFilterCriteria(prev => ({
                ...prev,
                isArchive: currentView === 'Archiwalne' ? true : false
            }))
        }
    }, [currentView])

    useEffect(() => {
        setPageNo(0)
    }, [JSON.stringify(ordersFilterCriteria), isSaving])

    useEffect(() => {
        setPageNoWzDocs(0)
    }, [JSON.stringify(wzDocsFilterCriteria)])

    useEffect(() => {
        const source = axios.CancelToken.source()
        const token = source.token

        handleGetOrders(ordersFilterCriteria, pageNo, token)

        return () => {
            source.cancel()
        }
    }, [pageNo, JSON.stringify(ordersFilterCriteria)])

    useEffect(() => {
        const source = axios.CancelToken.source()
        const token = source.token

        handleGetWzDoc(wzDocsFilterCriteria, pageNoWzDocs, token)

        return () => {
            source.cancel()
        }
    }, [pageNoWzDocs, JSON.stringify(wzDocsFilterCriteria)])

    const handleOrderSelectedOrdersItems = () => {
        if (selectedOrders.length === 0) {
            toast.warning('Proszę wybrać zamówienie')
        }
        else {
            if (cartItems && cartItems.basketItems && cartItems.basketItems.length > 0) {
                setShowQuestionAboutReplaceBasket(true)
            }
            else {
                handleMessageModalResult('No')
            }
        }
    }

    const handleMessageModalResult = async (answer) => {
        setShowQuestionAboutReplaceBasket(false)
        if (answer === 'Cancel' || answer === null) {
            return
        }
        setIsLoadingBasket(true)
        var replaceBasket = answer === 'Yes' ? true : false
        var selectedOrdersIds = selectedOrders.map(x => x.ordersNumber)
        const res = await loadBasketFromOrders(selectedOrdersIds, replaceBasket)
        if (!res) {
            setIsLoadingBasket(false)
            toast.error('Błąd podczas dodawania pozycji zamówienia do koszyka!')
            return
        }
        setIsLoadingBasket(false)
        navigate('/Cart')
    }

    const handleNameChange = (order) => {
        setHandlingOrder({ ordersNumber: order.ordersNumber, custOrderNumber: order.custOrderNumber, siteId: order.siteId })
        setIsChangeNameVisible(true)
    }

    const handleCancelButtonClick = () => {
        setHandlingOrder({ ordersNumber: '', custOrderNumber: '', siteId: '' })
        setIsChangeNameVisible(false);
    }

    const handleSave = async () => {
        setIsSaving(true)
        const result = await changeCustOrderNumber(handlingOrder.ordersNumber, handlingOrder.custOrderNumber, authState.custId, handlingOrder.siteId)

        setOrders(prevOrders => prevOrders.map(order => {       // Sprawdź, czy to jest zamówienie, które chcemy zaktualizować
            if (order.ordersNumber === handlingOrder.ordersNumber) {         // Jeśli tak, zwróć NOWY obiekt z kopią starych danych i nową nazwą
                return { ...order, custOrderNumber: handlingOrder.custOrderNumber };
            } // Jeśli nie, zwróć oryginalny obiekt bez zmian
            return order;
        }));


        setIsSaving(false)
        if (result) {
            setHandlingOrder({ ordersNumber: '', custOrderNumber: '', siteId: '' })
            setIsChangeNameVisible(false)
        } else {
            setError("Błąd podczas zmiany nazwy zamówienia!")
        }
    }

    const handleEditName = (e) => {
        const { name, value } = e.target;
        setError('')
        setHandlingOrder({
            ...handlingOrder,
            [name]: value
        })
    }

    if (!isUserHasAccess(UserRole.Orders)) {
        return <NoAccess />
    }
    if (isLoadingBasket) {
        return <LoadingSpinner loadingText="Wczytywanie koszyka..." />
    }
    return (
        <>
            <ErrorBoundary>

                <ScrollUpArrow />
                <YesNoCancelMessageModal
                    show={showQuestionAboutReplaceBasket}
                    onHide={handleMessageModalResult}
                    title={'Pytanie'}
                    message={'Twój koszyka zawiera już pozycje, co chcesz zrobić z zaznaczonymi pozycjami, które dodajesz do koszyka?'}
                    yesText='Nadpisz koszyk'
                    noText='Dopisz do koszyka'
                />

                <div className="container-fluid-with-paddings mt-4" style={{ marginBottom: isMobile ? '80px' : '0px' }}>
                    {isMobile && <div className="mb-2"><MobileAccountNavBar currentKey={AccountCurrentView.Zamowienia} /></div>}
                    <div className="main-container-holder">
                        <OrderTabsSelector
                            currentView={currentView}
                            setCurrentView={setCurrentView}
                        />
                        <ChangeOrderNumberModal
                            isChangeNameVisible={isChangeNameVisible}
                            handleCancelButtonClick={handleCancelButtonClick}
                            isSaving={isSaving}
                            custOrderNumber={handlingOrder.custOrderNumber}
                            handleEditName={handleEditName}
                            handleSave={handleSave}
                            error={error}
                        />
                        {currentView === 'WZ' ? (
                            <WzDocs
                                wzDocs={wzDocs}
                                isLoading={isLoadingWzDoc}
                                wzDocsFilterCriteria={wzDocsFilterCriteria}
                                setWzDocsFilterCriteria={setWzDocsFilterCriteria}
                            />
                        ) :
                            <Orders
                                orders={orders}
                                isArchive={currentView === 'Archiwalne'}
                                isLoading={isLoadingOrders}
                                onChangeNameButtonClick={handleNameChange}
                                setSelectedOrders={setSelectedOrders}
                                ordersFilterCriteria={ordersFilterCriteria}
                                setOrdersFilterCriteria={setOrdersFilterCriteria}
                                lastBookElementRef={lastBookElementRef}
                            />
                        }
                    </div>
                </div>

                <div className="row orders-page-bottom-panel">
                    {currentView !== 'WZ' &&
                        <div className='d-flex ms-5 mb-2 mt-3'>
                            <button className='btn-app-secondary' onClick={handleOrderSelectedOrdersItems}>
                                Zamów ponownie <IoCartOutline size={25} />
                            </button>
                        </div>
                    }
                </div>
            </ErrorBoundary>
        </>
    )
}

export default OrdersPageIndex